import React, { useCallback, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { ANALYSIS_MARGIN, DATASET_INSTANCE_HEADER, NAV_BAR, PAGE_MARGIN_PADDING_BORDER, useSplitView } from '../../../../hooks/useSplitView';
import { getDocumentAnalysisView } from '../../../documents/my-documents/store';
import { NettingEngineDocumentSummary } from '../../../opinions/netting-engine/documents/NettingEngineDocumentSummary';
import { calculateNettingEngineStarted } from '../../../opinions/netting-engine/store/actions';
import { getCurrentNettingEngineDocumentIsValid, getIsOpening as getIsOpeningNettingSummary } from '../../../opinions/netting-engine/store/selectors';
import { Button } from '../../../shared/button/Button';
import { Engine } from '../../../shared/icons';
import { PageLoadingDots } from '../../../shared/loading-dots/PageLoadingDots';
import { ModalHeader } from '../../../shared/modal/ModalHeader';
import { DatasetModal } from '../modal-instance/DatasetModal';
import styles from './DocumentNettingAnalysis.module.scss';
import { ArkDocument } from '../../../documents/store/types';
import { getInstanceUpdated } from '../store';

const MODAL_HEADER_HEIGHT = 78;
const MODAL_PADDING = 20;
const TITLE_MARGIN = 40;
const BUTTON_WRAPPER = 48;
const heightOffset = NAV_BAR + MODAL_HEADER_HEIGHT + MODAL_PADDING + TITLE_MARGIN + BUTTON_WRAPPER;
const widthOffset = PAGE_MARGIN_PADDING_BORDER;

interface SummaryModalProps {
    isOpen: boolean;
    closeModal: () => void;
    originalDocument: ArkDocument;
}

export const SummaryModal: React.FC<SummaryModalProps> = ({ isOpen, closeModal, originalDocument }) => {
    const dispatch = useAppDispatch();
    const [autoUpdateOnCalculate, setAutoUpdateOnCalculate] = useState<boolean>(false);

    const { entityA, entityB } = originalDocument;

    const isOpeningNettingSummary = useAppSelector(getIsOpeningNettingSummary);
    const documentAnalysisView = useAppSelector(getDocumentAnalysisView);
    const currentNettingEngineDocumentIsValid = useAppSelector(getCurrentNettingEngineDocumentIsValid);
    const instanceHasUpdated = useAppSelector(getInstanceUpdated);

    const [widthNumber, heightNumber] = useSplitView(documentAnalysisView, widthOffset, heightOffset);

    const height = useMemo(() => `${heightNumber}px`, [heightNumber]);
    const width = useMemo(() => `${widthNumber}px`, [widthNumber]);

    const documentEntities = useMemo(() => [entityA!, entityB!], [entityA, entityB]);

    const calculate = useCallback(() => dispatch(calculateNettingEngineStarted(autoUpdateOnCalculate)), [dispatch, autoUpdateOnCalculate]);

    const summaryContent = useMemo(() => {
        if (isOpeningNettingSummary) {
            return <PageLoadingDots />;
        }
        return (
            <NettingEngineDocumentSummary
                documentEntities={documentEntities}
                autoUpdateOnCalculate={autoUpdateOnCalculate}
                setAutoUpdateOnCalculate={setAutoUpdateOnCalculate}
                autoUpdateDisabled={instanceHasUpdated}
            />
        );
    }, [isOpeningNettingSummary, autoUpdateOnCalculate, setAutoUpdateOnCalculate, documentEntities, instanceHasUpdated]);

    return (
        <DatasetModal
            isOpen={isOpen}
            closeModal={closeModal}
            topOffset={`${DATASET_INSTANCE_HEADER + NAV_BAR + ANALYSIS_MARGIN}px`}
        >
            <div className={styles.documentNettingAnalysisWrapper} data-testid='document-netting-analysis-wrapper' style={{ width }}>
                <ModalHeader label='Netting Summary' icon={Engine} />
                <div className={styles.documentNettingAnalysis}>
                    <div className={styles.viewWrapper} style={{ height }}>
                        {summaryContent}
                    </div>
                    <div className={styles.buttonWrapper}>
                        <div className={styles.leftButtonWrapper}>
                            <Button onClick={closeModal} label='Close' />
                        </div>
                        <div className={styles.rightButtonWrapper}>
                            <Button label='Calculate' disabled={!currentNettingEngineDocumentIsValid} onClick={calculate} />
                        </div>
                    </div>
                </div>
            </div>
        </DatasetModal>
    );
};
